import { last, maxBy } from "lodash"

/**
 * Generate a React Query cache key for AI teammate messages
 */
export function getMessagesQueryKey(accountAlias, threadId) {
  return ["ai-teammate-messages", accountAlias, threadId]
}

/**
 * Get the total count of messages from paginated data
 */
export function getTotalCountFromPages(pages) {
  return maxBy(pages, "totalCount")?.totalCount || 0
}

/**
 * Add new messages to the React Query cache
 * Messages are added to the first page since pages are reversed (newest first)
 */
export function addMessagesToCache(queryClient, queryKey, newMessages) {
  queryClient.setQueryData(queryKey, (oldData) => {
    // If there is no data in the cache, this is a new thread, so we need to initialize the cache
    if (!oldData) {
      return {
        pages: [{ messages: newMessages, title: newMessages[0]?.text || "New Chat" }],
        pageParams: [null],
      }
    }

    // Optimistically update the total count of messages (which would get updated when a new page is fetched)
    const newTotalCount = getTotalCountFromPages(oldData.pages) + newMessages.length

    // If there is data in the cache, we need to add the new messages to the first page since they are reversed
    const [firstPage, ...restPages] = oldData.pages
    const newFirstPageMessages = [
      ...firstPage.messages,
      ...newMessages,
    ]
    const newFirstPage = {
      ...firstPage,
      messages: newFirstPageMessages,
      totalCount: newTotalCount,
    }
    const newPages = [newFirstPage, ...restPages]

    return {
      pages: newPages,
      pageParams: oldData.pageParams,
    }
  })
}

/**
 * Removes the optimistic message from the cache
 */
export function removeOptimisticMessages(queryClient, queryKey) {
  // If the last part of the query key is null, this is a new thread, so reset the cache
  if (last(queryKey) === null) {
    queryClient.resetQueries({ queryKey })
    return
  }

  queryClient.setQueryData(queryKey, (oldData) => {
    if (!oldData) {
      return oldData
    }

    const [firstPage, ...restPages] = oldData.pages

    // Remove the optimistic messages
    const cleanedMessages = firstPage.messages.filter(msg => !msg.isOptimistic)

    const countDelta = firstPage.messages.length - cleanedMessages.length
    const newTotalCount = getTotalCountFromPages(oldData.pages) - countDelta

    return {
      pages: [
        { ...firstPage, messages: cleanedMessages, totalCount: newTotalCount },
        ...restPages,
      ],
      pageParams: oldData.pageParams,
    }
  })
}

/**
 * Create an optimistic user message
 */
export function createOptimisticMessage(messageText, messageId) {
  return {
    id: messageId,
    createdAt: new Date().toISOString(),
    text: messageText,
    role: "user",
    isOptimistic: true,
  }
}
